import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-warm via-white to-neutral-warm overflow-hidden pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-hero-pattern"></div>
      
      {/* Animated Background Elements */}
      <motion.div 
        className="absolute top-20 left-20 w-32 h-32 bg-ember-spark/10 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div 
        className="absolute top-40 right-32 w-24 h-24 bg-ember-fire/10 rounded-full blur-2xl"
        animate={{ 
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.7, 0.4]
        }}
        transition={{ duration: 3, repeat: Infinity, delay: 1 }}
      />
      <motion.div 
        className="absolute bottom-32 left-1/3 w-40 h-40 bg-ember-depth/10 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.5, 0.2]
        }}
        transition={{ duration: 5, repeat: Infinity, delay: 2 }}
      />

      {/* Hero Background Image */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
           style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1513759565286-20e9c5fad06b)' }}>
      </div>
      
      <motion.div 
        className="max-w-5xl mx-auto px-6 text-center relative z-10 pt-20"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Main Headline */}
        <motion.h1 
          className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8 px-4"
          variants={itemVariants}
        >
          <span className="block text-neutral-charcoal text-shadow">Leadership isn't a title</span>
          <span className="block bg-ember-gradient bg-clip-text text-transparent py-2">you earn—</span>
          <span className="block text-neutral-charcoal text-shadow">it's a flame you tend.</span>
        </motion.h1>
        
        {/* Description */}
        <motion.div 
          className="max-w-3xl mx-auto mb-12"
          variants={itemVariants}
        >
          <p className="text-xl text-neutral-medium leading-relaxed mb-6">
            In a world drowning in quick fixes and shallow connections, EMBER ignites something different. 
            We believe that true leadership begins not with strategy or systems, but with the courage to face yourself fully.
          </p>
          <motion.p 
            className="text-lg text-neutral-dark font-medium italic"
            whileHover={{ scale: 1.02 }}
          >
            <Flame className="inline-block w-5 h-5 mr-2 text-ember-fire" />
            The ember inside you is already glowing.
          </motion.p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div 
          className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
          variants={itemVariants}
        >
          <a href="#ignite-transformation">
            <motion.button 
              className="btn-primary text-lg px-12 py-4 ember-glow group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Ready to stop leading alone?
              <ArrowRight className="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;